/**
 * `@dxf-ai-editor/command-engine` — the ONE truth for a legacy heavy
 * `POLYLINE`'s **Length** and **Area** property rows (@codex PR #785 round 5,
 * FINDING B).
 *
 * # The defect this replaces
 *
 * Both projections of these two rows — the typed descriptor
 * (`descriptors/legacyPolylineTableOle.ts`) and the single-select preview bag
 * (`apps/web/.../state/dxfGeometry.ts::buildPolylinePreview`) — used to hand
 * `entity.vertices` VERBATIM to the shared chord/shoelace helpers, i.e. they
 * treated the whole RAW VERTEX RECORD ARRAY as one open or closed path. A heavy
 * POLYLINE's record array is not a path for two of its four group-70 subtypes,
 * and not the whole path for a third:
 *
 *   - a **polygon mesh** (bit 16) is an M×N grid stored row-major, so walking
 *     the records in file order counts a link from the end of each row to the
 *     start of the next — segments that exist nowhere in the drawing;
 *   - a **poly-face mesh** (bit 64) additionally interleaves FACE records into
 *     the vertex pool, whose 10/20/30 is FILLER (AutoCAD writes (0, 0, 0))
 *     because the record's body is its 71..74 indices — so the walk also ran
 *     out to the filler location and back;
 *   - a **spline-fit** polyline (bit 4) stores the drawn approximation (VERTEX
 *     bit 8) AND the control FRAME (bit 16), so the raw walk drew a phantom
 *     span from the end of the curve back into the frame and then measured the
 *     frame as if it were more curve.
 *
 * With group-70 bit 1 also set on a mesh (it means "closed in M" there, not
 * "closed path") the Area row then ran the shoelace over that same record
 * order. Both numbers depended on STORAGE ORDER and on filler records rather
 * than on anything visible.
 *
 * # Which rows each subtype HAS — {@link subtypeMetricRows}
 *
 * Length and Area are PATH metrics of a polyline, and AutoCAD offers each one
 * only where it is DEFINED. That per-subtype answer is a TABLE in this file
 * ({@link subtypeMetricRows}), not a chain of conditions inside the
 * calculation: the table IS this doc in executable form, so a row cannot be
 * described here and computed differently three lines down, and a fifth
 * subtype would not compile until it is given an entry.
 *
 *   | group-70 subtype                      | Length     | Area |
 *   |---------------------------------------|------------|------|
 *   | 2D polyline (`AcDb2dPolyline`)        | in-plane   | yes  |
 *   | 3D polyline (`AcDb3dPolyline`, bit 8) | **3D**     | NO   |
 *   | polygon mesh (`AcDbPolygonMesh`)      | NO         | NO   |
 *   | poly-face mesh (`AcDbPolyFaceMesh`)   | NO         | NO   |
 *
 * **The meshes have neither.** AutoCAD's palette Geometry section for a mesh is
 * the per-VERTEX navigator (Vertex / Vertex X / Vertex Y / Vertex Z) with the
 * mesh structure in Misc (M and N vertex counts, smooth densities, surface
 * type) — there is no Length row and no Area row, and the AREA command does not
 * accept a mesh either. A mesh is a SURFACE: its records describe faces, not a
 * traversal order, so no path length exists to report.
 *
 * **A 3D polyline has a Length but NO Area.** Its vertices carry independent
 * WCS Z, so the polygon they close is in general NON-PLANAR and simply does not
 * bound a planar region — AutoCAD's palette shows Length for `AcDb3dPolyline`
 * and no Area row at all, and AREA / `LIST` likewise decline it. The XY
 * shoelace of those vertices is NOT that missing number: it is the area of the
 * polygon's SHADOW on the world XY plane, which depends on the viewing plane
 * rather than on the entity, and which would print a confident `50` for a
 * closed skew triangle through (0, 0, 0), (10, 0, 1), (0, 10, 2) that encloses
 * no such area (@codex PR #785 round 6). A flat 3D polyline is not an
 * exception the other way: AutoCAD gates the row on the SUBTYPE, not on a
 * coplanarity test, so the row stays empty even when the vertices happen to
 * share a Z — anything else would make a property row appear and disappear as
 * a vertex is dragged.
 *
 * Inventing either number anyway (summing a mesh's wireframe runs, projecting a
 * 3D polyline's shadow) would be a value AutoCAD never shows, i.e. exactly the
 * fabrication CLAUDE.md ПРИОРИТЕТ #1 forbids — so
 * {@link heavyPolylinePathMetrics} returns `undefined` for the whole pair on a
 * mesh and `area: undefined` on a 3D polyline, and BOTH projections render that
 * row EMPTY. That is the same visible, deliberate "not applicable to this
 * subtype" projection the mesh rows already use in the other direction
 * (`M vertex count` and friends read empty on a plain polyline); it is never a
 * zero, which would read as a measured value.
 *
 * # Where the record→path rule comes from
 *
 * Not from here: {@link heavyPolylineRuns} (`@dxf-ai-editor/cad-core`) is the
 * established single truth for WHICH VERTEX records are visible geometry and in
 * what order — the same one the renderer (`scene3d/adapters/typed/
 * polylineFromEntity.ts`), the picker (`cad-core/selectLeaf.ts`), the
 * vector-PDF plot (`print/pdf/converters/heavyPolylineConverter.ts`) and the AI
 * OSNAP route (`ai/sceneQuery/osnapQuery.ts`) read. (NOT the WCS bounds walk:
 * `drawing-model/wcsBoundsLeaf.ts::heavyPolylineLocal` re-derives its own
 * record filter, because `drawing-model` is what `cad-core` is built ON and
 * importing back would be a cycle. Its box therefore still spans a spline-fit
 * polyline's control FRAME. That is a bounds question, tracked where the bounds
 * are, and it does not reach these two rows.)
 *
 * This module only decides which metric each subtype HAS, and hands the
 * resolved run — positions AND its group-42 bulge channel — to the shared
 * `computePolylineLength` / `computePolylineArea` helpers every polyline
 * family already measures with (LWPOLYLINE's own Length / Area rows in
 * `descriptors/curves.ts` call the same two functions, and became arc-aware in
 * the same change).
 *
 * The run's points go in as the records' OWN x/y — deliberately NOT lifted
 * through `heavyPolylineWcsResolver`. For a 2D polyline those are OCS
 * coordinates in the group-210 plane, and the OCS basis is ORTHONORMAL: chord
 * lengths and the shoelace are identical in the entity's plane and in WCS, so
 * the in-plane measure AutoCAD reports is exactly what the raw pair yields,
 * while a lift would instead demand the plane's own basis to shoelace in (the
 * WCS x/y of a tilted polyline are a projection, not its plane). A 3D
 * polyline's records are already WCS, and it has no Area row at all.
 */

import { heavyPolylineRuns } from "@dxf-ai-editor/cad-core";
import {
  polylineSubtype,
  type PolylineEntity,
  type PolylineSubtype,
} from "@dxf-ai-editor/drawing-model";

import {
  computePolylineArea,
  computePolylineLength,
} from "./geometryEntityTypes";

/** The two path-metric property rows, for a subtype that HAS a path. */
export interface HeavyPolylinePathMetrics {
  /**
   * Path length of the visible run, with every bulged span measured as the ARC
   * it is drawn as and every straight one as its chord. THREE-dimensional for a 3D polyline
   * (group 70 bit 8), whose vertices carry real WCS Z — a sloped span is longer
   * than its XY projection and a purely vertical one is not zero-length; planar
   * for a 2D polyline, whose vertex Z is the entity's own elevation repeated
   * and which AutoCAD measures in its plane.
   */
  readonly length: number;
  /**
   * Enclosed area of the visible run when it is CLOSED — its shoelace plus the
   * signed circular segment of every bulged span — and 0 when it is open —
   * and `undefined` for a subtype with NO defined planar area, which the
   * projections render as an EMPTY row exactly as they do the whole pair for a
   * mesh. That is the 3D polyline: its closing polygon is in general
   * non-planar, so the XY shoelace would be the area of its SHADOW rather than
   * of anything the entity bounds (see {@link subtypeMetricRows}).
   */
  readonly area: number | undefined;
}

/** Which rows a subtype has, and in how many dimensions its Length is taken. */
interface SubtypeMetricRows {
  /** THREE-dimensional chords (a 3D polyline) vs in-plane ones (a 2D one). */
  readonly lengthIn3D: boolean;
  /** Whether this subtype has a defined planar Area at all. */
  readonly hasArea: boolean;
}

/**
 * Which of the two rows each group-70 subtype HAS, and in how many dimensions
 * its Length is measured — the module doc's table, executable.
 *
 * `undefined` means the subtype has NO path at all (both rows empty);
 * `hasArea: false` means it has a path but no planar area to report (Length
 * only). The total `Record` over {@link PolylineSubtype} is the point: the
 * calculation below reads its answer from here instead of re-deciding it, so
 * the doc above and the code cannot drift apart the way round 6 found them,
 * and a FIFTH subtype would not compile until this table says what it shows.
 *
 * Built per call rather than hoisted to a module constant DELIBERATELY: a
 * module-level literal is a STATIC mutant, and the vitest runner's module
 * cache keeps the unmutated copy alive across mutant runs, so targeted
 * mutation testing cannot kill a flipped row — every one of these seven
 * mutants (`hasArea: true → false` on the 2D row included) SURVIVED while the
 * table was hoisted, even though the tests that would catch it exist and pass.
 * A table the gate cannot check is exactly the drift this module is now
 * supposed to make impossible; one object allocation per property read is not
 * a price worth arguing about next to that.
 */
function subtypeMetricRows(
  subtype: PolylineSubtype,
): SubtypeMetricRows | undefined {
  const rows: Record<PolylineSubtype, SubtypeMetricRows | undefined> = {
    "2d": { lengthIn3D: false, hasArea: true },
    "3d": { lengthIn3D: true, hasArea: false },
    "polygon-mesh": undefined,
    "polyface-mesh": undefined,
  };
  return rows[subtype];
}

/**
 * The Length / Area pair for a heavy POLYLINE, or `undefined` when the subtype
 * has no path metric at all (polygon mesh / poly-face mesh — see the module
 * doc). Callers MUST render an EMPTY row for `undefined`, never `0` — and the
 * same for an `undefined` {@link HeavyPolylinePathMetrics.area}, which is how a
 * subtype that has a Length but no defined planar area (the 3D polyline) leaves
 * its Area row empty. Both are read from {@link subtypeMetricRows}.
 *
 * BULGE (@codex PR #785 round 13, FINDING B): a span whose group-42 bulge is
 * nonzero is an ARC, and both rows now measure it as one. The fix is in the
 * SHARED helpers (`geometryEntityTypes.ts`, which read the `bulge` a vertex
 * carries and resolve it through `drawing-model`'s one `bulgeArc` primitive),
 * exactly as the previous revision of this note said it had to be — so
 * LWPOLYLINE's rows became arc-aware in the same change and no second Length
 * truth for this family was created. All this module adds is the run's OWN
 * bulge channel, which it was dropping on the floor.
 */
export function heavyPolylinePathMetrics(
  entity: PolylineEntity,
): HeavyPolylinePathMetrics | undefined {
  const rows = subtypeMetricRows(polylineSubtype(entity));
  if (!rows) {
    return undefined;
  }
  // A non-mesh subtype resolves to exactly ONE run: `heavyPolylineRuns` fans
  // out only in its mesh branch, and the mesh branch is excluded above, so the
  // remaining path returns a single-element array literal BY CONSTRUCTION —
  // `[0]!` cannot be undefined here, and a `!run` guard would be unreachable
  // code no test could ever reach (it survived targeted mutation testing as
  // exactly that: a branch whose removal nothing can observe). An empty vertex
  // list still lands here with an EMPTY `indices` run and measures 0 (and an
  // area of 0 where the subtype has that row) — the degenerate case is handled
  // by the metric helpers, not by a special case.
  const run = heavyPolylineRuns(entity)[0]!;
  // `run.bulges[k]` is the group-42 bulge of the span LEAVING `indices[k]`
  // ({@link heavyPolylineRuns}'s contract), which is exactly what a
  // `PolylineMetricVertex` carries — so the run is handed over whole rather
  // than flattened to bare positions, and the ARC each bowed span really is
  // reaches the two metrics. The channel is already 0 for every subtype that
  // cannot bulge (group 42 is a 2D-polyline field), so a 3D polyline's Length
  // stays the pure 3D chord walk it was.
  const points = run.indices.map((i, k) => {
    const v = entity.vertices[i]!;
    return {
      x: v.location.x,
      y: v.location.y,
      z: v.location.z ?? 0,
      bulge: run.bulges[k] ?? 0,
    };
  });
  const planar = points.map((p) => ({ x: p.x, y: p.y, bulge: p.bulge }));
  return {
    length: computePolylineLength(
      rows.lengthIn3D ? points : planar,
      run.closed,
    ),
    // NOT `computePolylineArea(...) : 0` — a 0 reads as a measured value, and
    // "this subtype has no area" is `undefined`, the empty row.
    area: rows.hasArea ? computePolylineArea(planar, run.closed) : undefined,
  };
}
